import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import { whatsappConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWhatsappConversations";
import { instagramConversations$ } from "@jugl-web/rest-api/integrations/hooks/useInstagramConversations";
import { webChatBotConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWebChatBotConversations";
import { twilioConversations$ } from "@jugl-web/rest-api/integrations/hooks/useTwilioConversations";
import { emailConversations$ } from "@jugl-web/rest-api/integrations/hooks/useEmailConversations";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useCallback, useMemo } from "react";
import { useClientSupportPageContext } from "../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ManageContactAlertVariant } from "../components/ManageContactAlert";
import { getEmailConversationContactName } from "../utils/getEmailConversationContactName";

export const useManageContact = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const {
    selectedConversation,
    refetchTrigger$,
    selectConversation,
    selectedChannel,
  } = useClientSupportPageContext();

  const [manageContact, { isLoading: isManageContactLoading }] =
    integrationsApi.useManageContactMutation();

  const contactName = useMemo(() => {
    if (!selectedConversation) return "";

    if (selectedChannel === CRMChannel.email) {
      return getEmailConversationContactName(selectedConversation);
    }

    return selectedConversation.contact_info?.name || selectedConversation.name;
  }, [selectedChannel, selectedConversation]);

  const showManageContactToast = useCallback(
    (type: ManageContactAlertVariant) => {
      if (type === "block") {
        toast(
          <span>
            {t(
              {
                id: "client-support-page.customer-was-blocked",
                defaultMessage: "{name} was blocked",
              },
              {
                name: <span className="font-semibold">{contactName}</span>,
              }
            )}
          </span>
        );
        return;
      }

      toast(
        <span>
          {t(
            {
              id: "client-support-page.customer-was-unblocked",
              defaultMessage: "{name} was unblocked",
            },
            {
              name: <span className="font-semibold">{contactName}</span>,
            }
          )}
        </span>
      );
    },
    [contactName, t, toast]
  );

  const handleManageContact = useCallback(
    async (actionType: ManageContactAlertVariant) => {
      if (!selectedConversation) {
        return;
      }

      const response = await manageContact({
        entityId,
        userId: selectedConversation.conv_id,
        type: actionType,
      });

      if (response && "data" in response) {
        showManageContactToast(actionType);
        const conversationId = selectedConversation.conv_id;

        switch (selectedChannel) {
          case CRMChannel.whatsapp:
            whatsappConversations$.next({ type: "remove", conversationId });
            break;
          case CRMChannel.instagram:
            instagramConversations$.next({ type: "remove", conversationId });
            break;
          case CRMChannel.chatbot:
            webChatBotConversations$.next({ type: "remove", conversationId });
            break;
          case CRMChannel.twilio:
            twilioConversations$.next({ type: "remove", conversationId });
            break;
          case CRMChannel.email:
            emailConversations$.next({ type: "remove", conversationId });
            break;
          default:
            break;
        }

        refetchTrigger$.next();
        selectConversation(undefined);
      }
    },
    [
      selectedConversation,
      refetchTrigger$,
      manageContact,
      entityId,
      selectConversation,
      selectedChannel,
      showManageContactToast,
    ]
  );

  return {
    contactName,
    isManageContactLoading,
    handleManageContact,
  };
};
